export type BannerContentType = "fullscreen" | "mrec" | "banner";

const MREC_ASPECT_RATIOS = new Set([
  "6:5", // 300x250
  "32:25", // 320x250
  "4:15", // 160x600
  "1:2", // 300x600
  "1:5", // 120x600
]);

function greatestCommonDivisor(a: number, b: number): number {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y !== 0) {
    const next = x % y;
    x = y;
    y = next;
  }
  return x;
}

function aspectRatioKey(width: number, height: number): string {
  const divisor = greatestCommonDivisor(width, height);
  return `${width / divisor}:${height / divisor}`;
}

function isMRECAspectRatio(width: number, height: number): boolean {
  if (width <= 0 || height <= 0) {
    return false;
  }
  return MREC_ASPECT_RATIOS.has(aspectRatioKey(width, height));
}

/**
 * Classifies banner dimensions for TrePreview chrome.
 *
 * Non-banner creatives matching the supported IAB fixed display aspect ratios
 * render as inline MREC slots; remaining creatives use fullscreen chrome.
 */
export function classifyBannerDimensions(
  width: number,
  height: number
): BannerContentType {
  if (height <= 90) {
    return "banner";
  }
  if (isMRECAspectRatio(width, height)) {
    return "mrec";
  }
  return "fullscreen";
}

/** Leaderboard / tablet banner units should default to tablet device chrome. */
export function shouldDefaultTabletDevice(
  width: number,
  height: number,
  deviceParam: string | null
): boolean {
  if (deviceParam === "tablet") {
    return true;
  }
  return width >= 728 && height <= 90;
}

export function parsePositiveInt(value: string | null): number | undefined {
  if (value == null || value === "") {
    return undefined;
  }
  const n = Number.parseInt(value, 10);
  if (!Number.isFinite(n) || n <= 0) {
    return undefined;
  }
  return n;
}

/** Default iframe dimensions when `size` is set without `w`/`h` (legacy URLs). */
export function defaultSlotDimensions(contentType: "banner" | "mrec"): {
  width: number;
  height: number;
} {
  if (contentType === "banner") {
    return { width: 320, height: 50 };
  }
  return { width: 300, height: 250 };
}

export function applySlotIframeStyles(
  contentType: "banner" | "mrec",
  width: number,
  height: number
): void {
  const iframeWrappers = document.querySelectorAll(".iframe-wrapper");
  iframeWrappers.forEach((wrapper) => {
    const el = wrapper as HTMLElement;
    if (contentType === "banner") {
      el.style.setProperty("--iframe-width", "100%");
      el.style.setProperty("--iframe-height", `${height}px`);
    } else {
      el.style.setProperty("--iframe-width", `${width}px`);
      el.style.setProperty("--iframe-height", `${height}px`);
    }
  });
}

export function clearSlotIframeStyles(): void {
  const iframeWrappers = document.querySelectorAll(".iframe-wrapper");
  iframeWrappers.forEach((wrapper) => {
    const el = wrapper as HTMLElement;
    el.style.removeProperty("--iframe-width");
    el.style.removeProperty("--iframe-height");
  });
}
